import React, { useState, useEffect } from "react";
import { Table, Button, Input, Row, Col, Card, notification } from "antd";
import { useParams } from "react-router-dom";
import { formatKey, generateId } from "../Service";
import { TenantService, CensusShopService } from "../../../Services";
import isEqual from "lodash/isEqual";
import AddNewColumn from "./AddNewColumn";
export const CensusShopCardKeys = () => {
	const { tenantName } = useParams();

	const [columnLoading, setColumnLoading] = useState(false);
	const [uniqueKeys, setUniqueKeys] = useState([]);
	const [oldColumnsData, setOldColumnsData] = useState([]);
	const [columnsData, setColumnsData] = useState([]);
	const [deletedRows, setDeletedRows] = useState([]);

	useEffect(() => {
		const fetchTenantConfig = async () => {
			try {
				setColumnLoading(true);
				const { data } = await TenantService.getTenantCensusShopCardData({ tenantName });

				setColumnsData(data.censusShopCardData);
				setOldColumnsData(data.censusShopCardData);

				const { data: uniqueKeysData } = await CensusShopService.getTenantUniqueCensusKeys({ tenantName });
				const allKeys = [...(uniqueKeysData?.shopDataKeys || []), ...(uniqueKeysData?.tenantKeys || [])].filter(
					key => {
						return !data.censusShopCardData.some(column => column.key === key);
					}
				);
				setUniqueKeys(allKeys);
				setColumnLoading(false);
			} catch (error) {
				notification.error({ message: "Error fetching Report Columns" });
				console.error("Error fetching tenant config:", error);
			}
		};
		fetchTenantConfig();
	}, [tenantName]);

	// Function to add a new row
	const addRow = columnNames => {
		const newCoumns = columnNames.map(columnName => ({
			key: columnName,
			name: formatKey(columnName),
			_id: generateId()
		}));

		setColumnsData([...newCoumns, ...columnsData]);
		setUniqueKeys(uniqueKeys.filter(key => !columnNames.includes(key)));
	};

	// Function to delete a row
	const deleteRow = _id => {
		const rowToDelete = columnsData.find(row => row._id === _id);
		setDeletedRows([...deletedRows, rowToDelete]); // Save deleted rows for undo
		setColumnsData(columnsData.filter(row => row._id !== _id));
		setUniqueKeys([...uniqueKeys, rowToDelete.key]);
	};

	// Function to undo the last deletion
	const undoDelete = () => {
		if (deletedRows.length > 0) {
			const restoredRow = deletedRows[deletedRows.length - 1];
			setColumnsData([...columnsData, restoredRow]);
			setDeletedRows(deletedRows.slice(0, -1));
			setUniqueKeys(uniqueKeys.filter(key => key !== restoredRow.key));
		}
	};

	// Update the key or name in the table
	const updateRow = (_id, field, value) => {
		setColumnsData(columnsData.map(row => (row._id === _id ? { ...row, [field]: value } : row)));
	};
	// Define table columns
	const columns = [
		{
			title: "Column Key (DB Key)",
			dataIndex: "key",
			render: (text, record) => (
				<Input
					value={text}
					onChange={e => updateRow(record._id, "key", e.target.value)}
					placeholder="Enter column key"
				/>
			)
		},
		{
			title: "Column Name (Display Name)",
			dataIndex: "name",
			render: (text, record) => (
				<Input
					value={text}
					onChange={e => updateRow(record._id, "name", e.target.value)}
					placeholder="Enter display name"
				/>
			)
		},
		{
			title: "Action",
			render: (_, record) => (
				<Button onClick={() => deleteRow(record._id)} type="link">
					Delete
				</Button>
			)
		}
	];
	return (
		<Card title="Census Shop Card Data">
			<Row>
				<Col span={24}>
					<Row justify="space-between" style={{ marginBottom: "16px" }}>
						<Col>
							<AddNewColumn availableColumn={uniqueKeys} addRow={addRow} columnLoading={columnLoading} />
						</Col>
						<Col>
							<Button
								onClick={() => {
									setColumnsData(columnsData.map(row => ({ ...row, name: formatKey(row.key) })));
								}}
								disabled={columnsData.length === 0}
							>
								Auto Generate Display Name
							</Button>
						</Col>
						<Col>
							<Button type="primary" onClick={undoDelete} disabled={deletedRows.length === 0}>
								Undo Delete
							</Button>
						</Col>
					</Row>
				</Col>

				<Col span={24}>
					<Table
						dataSource={columnsData}
						columns={columns}
						rowKey="_id"
						pagination={false}
						bordered
						scroll={{ y: 400 }} // Optional: This can be used for vertical scrolling
					/>
				</Col>

				<Col span={24}>
					<Row justify="end" style={{ marginTop: "16px" }}>
						<Col>
							<Button
								disabled={isEqual(columnsData.sort(), oldColumnsData.sort())}
								type="primary"
								onClick={async () => {
									try {
										const { data } = await TenantService.updateTenantCensusShopCardData({
											tenantName,
											censusShopCardData: columnsData.map(({ key, name }) => ({ key, name }))
										});
										setColumnsData(data.censusShopCardData);
										setOldColumnsData(data.censusShopCardData);
										notification.success({ message: "Columns updated successfully" });
									} catch (error) {
										notification.error({ message: "Error updating columns" });
										console.error("Error updating columns:", error);
									}
								}}
							>
								Update Columns
							</Button>
						</Col>
					</Row>
				</Col>
			</Row>
		</Card>
	);
};

export default CensusShopCardKeys;
